import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useLocalStorage } from '@services/hooks.service';
import {
  firstSignIn,
  refreshTokens,
  signIn,
  signOut,
  testToken,
} from '@services/auth.service';
import { useUserDetails } from '@services/query.service';
import { setItem } from '@services/local-storage.service';

import AuthProviderContext from './auth-provider.context';
import { registerAxiosInterceptor } from './auth-provider.utils';

function AuthProvider({ children }) {
  const [checkedAuthentication, setCheckedAuthentication] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [rememberMe, setRememberMe] = useLocalStorage('rememberMe', false);
  const [registeredAxiosInterceptor, setRegisteredAxiosInterceptor] = useState(false);

  const userDetails = useUserDetails(isLoggedIn);

  const logOutMutation = useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      setIsLoggedIn(false);
      setRememberMe(false);
    },
  });

  const loginMutation = useMutation({
    mutationFn: async ({ email, password, userWantsToBeRemembered }) => {
      setRememberMe(userWantsToBeRemembered);
      const signInResult = await signIn({ email, password });

      if (signInResult.accessToken && signInResult.refreshToken) {
        setItem('accessToken', signInResult.accessToken);
        setItem('refreshToken', signInResult.refreshToken);
        setIsLoggedIn(true);
        return { groups: signInResult.groups };
      }

      return signInResult;
    },
  });

  const firstSignInMutation = useMutation({
    mutationFn: firstSignIn,
    onSuccess: (signInResult) => {
      setItem('accessToken', signInResult.accessToken);
      setItem('refreshToken', signInResult.refreshToken);
      setIsLoggedIn(true);
    },
  });

  function onAuthCompletion() {
    document.documentElement.setAttribute('data-app-initialised', true);
    setCheckedAuthentication(true);
  }

  useEffect(() => {
    if (!registeredAxiosInterceptor) {
      setRegisteredAxiosInterceptor(true);
      registerAxiosInterceptor(logOutMutation.mutate);
    }
  }, [logOutMutation, registeredAxiosInterceptor]);

  useEffect(() => {
    if (!checkedAuthentication) {
      if (!rememberMe) {
        testToken()
          .then(() => setIsLoggedIn(true))
          .finally(onAuthCompletion);
      } else {
        testToken()
          .then(() => setIsLoggedIn(true))
          // user access token invalid => check if we can use refresh token
          .catch(() => refreshTokens().then(() => setIsLoggedIn(true)))
          .finally(onAuthCompletion);
      }
    }
  }, [rememberMe, checkedAuthentication]);

  return (
    <AuthProviderContext
      value={{
        logIn: loginMutation.mutateAsync,
        logOut: logOutMutation.mutate,
        isLoggedIn,
        isLoggingIn: loginMutation.isPending,
        isLoginError: loginMutation.isError,
        loginError: loginMutation.error,
        loading: !checkedAuthentication,
        user: isLoggedIn ? userDetails.data : null,
        firstSignInMutation,
      }}
    >
      {children}
    </AuthProviderContext>
  );
}

export default AuthProvider;